import React, { useCallback, useState } from 'react';
import _ from 'lodash';
import Stack from 'ui-kit/Stack';

import { Button, Typography } from 'ui-kit';
import ConfirmModal from 'components/ConfirmModal';
import { useFormContext, useWatch } from 'react-hook-form';
import { message } from 'antd';
import { createStores } from 'api/store';
import { ImportedStore } from 'api/types/store';
import { EImportStoreActionType } from '../constants';

export interface ICreateStoresButtonConfirmModal {
  ownerId: number;
  setIsCreatingStores: React.Dispatch<React.SetStateAction<boolean>>;
  setMassStoreRequestId: React.Dispatch<React.SetStateAction<number | null>>;
  onFinishSync: () => void;
}

const CreateStoresButtonConfirmModal = ({
  ownerId,
  setIsCreatingStores,
  setMassStoreRequestId,
  onFinishSync,
}: ICreateStoresButtonConfirmModal) => {
  const [isOpen, setIsOpen] = useState(false);

  const { control, trigger } = useFormContext();

  const formData = useWatch({ control, name: 'listings' });

  // const isDisabled = useMemo(() => selectedListings.length === 0, [selectedListings]);

  const submit = useCallback(() => {
    setIsCreatingStores(true);
    const selectedListingsForSave = formData
      .filter(
        (listing: ImportedStore) =>
          listing.action !== EImportStoreActionType.DISCONNECT ||
          (listing.action === EImportStoreActionType.DISCONNECT &&
            !_.isNil(listing.existingImportSource)),
      )
      .map((listing: ImportedStore) => ({
        ...listing,
        storeId:
          listing.action === EImportStoreActionType.DISCONNECT
            ? listing.existingImportSource.storeId
            : listing.storeId,
      }));

    createStores({ owner: ownerId, stores: selectedListingsForSave })
      .then((response) => {
        if (response.ok) {
          if (response.data?.massStoreRequestId) {
            setMassStoreRequestId(response.data?.massStoreRequestId);
          } else if (response.data?.isFinishedSync) {
            onFinishSync();
          }
        } else {
          setIsCreatingStores(false);
          message.error(
            response?.errors?.fields?.[0] || 'Something went wrong creating your stores.',
            10,
          );
        }
      })
      .finally(() => setIsOpen(false));
  }, [ownerId, formData, setIsCreatingStores, setMassStoreRequestId, onFinishSync]);

  const handleCreateClick = useCallback(() => {
    trigger('listings').then((isValid) => {
      if (isValid) {
        setIsOpen(true);
      } else {
        message.error('Your selected listings have errors. Please fix them before continuing.');
      }
    });
  }, [trigger]);

  return (
    <>
      <Button variant="contained" onClick={handleCreateClick}>
        Apply changes
      </Button>

      <ConfirmModal
        visible={isOpen}
        confirmBtnType="primary"
        confirmText="Create"
        cancelText="Cancel"
        onCancel={() => setIsOpen(false)}
        onConfirm={submit}
        destroyOnClose
      >
        <Typography variant="h4">Create stores</Typography>
        <Stack
          flexDirection="column"
          alignItems="flex-start"
          style={{ textAlign: 'left', marginTop: '1rem' }}
          gap="8px"
        >
          <Typography variant="body1">
            If store address or postal code is missing, store will default to USA, $USD, and
            nationally available vendors.
          </Typography>
          <Typography variant="body1">
            If the store description is too long it will be truncated after 500 characters.
          </Typography>
          <Typography variant="body1">
            If the store name is too long it will be truncated after 75 characters.
          </Typography>
          <Typography variant="body1" style={{ fontWeight: 600 }}>
            Update info per store using Manage &gt; Edit.
          </Typography>
        </Stack>
      </ConfirmModal>
    </>
  );
};
export default CreateStoresButtonConfirmModal;
